import { forwardRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBold,
  faCode,
  faH1,
  faH2,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faStrikethrough,
} from "@fortawesome/pro-solid-svg-icons";
import { twJoin, twMerge } from "tailwind-merge";

import { Icon, IconSize } from "src/design-system/Icon";

const ToolbarItem: React.FC<{
  icon: IconProp;
  type: string;
  value?: string;
  size?: IconSize;
}> = ({ icon, type, value, size = "xs" }) => (
  <li>
    <button
      className={twJoin(type, "rounded")}
      type="button"
      value={value}
      // Prevent the mousedown default so the editor never blurs: Quill's
      // toolbar click handler relies on `savedRange` and calls `focus()` to
      // restore it, but the intermediate blur can race with browser
      // `selectionchange` events and leave the selection collapsed at index 0
      // (so formatting gets applied to the first line instead of the
      // user's selection).
      onMouseDown={(e) => e.preventDefault()}
    >
      <Icon icon={icon} size={size} />
    </button>
  </li>
);

type Props = {
  faded?: boolean;
  exclude?: (
    | "heading"
    | "bold"
    | "italic"
    | "strikethrough"
    | "link"
    | "listUl"
    | "listOl"
    | "code"
  )[];
};

export const Toolbar = forwardRef<HTMLUListElement, Props>(
  ({ faded, exclude = [] }, ref) => {
    const controlledFaded = faded !== undefined;
    const controlledFadedClass = faded === true ? "opacity-50" : "opacity-100";

    return (
      <ul
        ref={ref}
        className={twMerge(
          "flex items-center gap-x-1",
          controlledFaded
            ? controlledFadedClass
            : "opacity-50 group-focus-within:opacity-100",
        )}
      >
        {!exclude.includes("heading") && (
          <>
            <ToolbarItem icon={faH1} size="sm" type="ql-header" value="1" />
            <ToolbarItem icon={faH2} size="sm" type="ql-header" value="2" />
          </>
        )}
        {!exclude.includes("bold") && (
          <ToolbarItem icon={faBold} type="ql-bold" />
        )}
        {!exclude.includes("italic") && (
          <ToolbarItem icon={faItalic} type="ql-italic" />
        )}
        {!exclude.includes("strikethrough") && (
          <ToolbarItem icon={faStrikethrough} type="ql-strike" />
        )}
        {!exclude.includes("link") && (
          <ToolbarItem icon={faLink} type="ql-link" />
        )}
        <li className="text-gray-200">|</li>
        {!exclude.includes("listUl") && (
          <ToolbarItem icon={faListUl} type="ql-list" value="bullet" />
        )}
        {!exclude.includes("listOl") && (
          <ToolbarItem icon={faListOl} type="ql-list" value="ordered" />
        )}
        {!exclude.includes("code") && (
          <ToolbarItem icon={faCode} type="ql-code" />
        )}
      </ul>
    );
  },
);
